import { useEffect, useRef, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { MessageCircle, Send } from "lucide-react";
import { pickColor } from "@/lib/palette";

function getBackendWsUrl(rideId, token) {
  const base = process.env.REACT_APP_BACKEND_URL || "";
  let url;
  try {
    url = new URL(base);
  } catch {
    url = new URL(window.location.origin);
  }
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  url.pathname = `/api/ws/rides/${rideId}`;
  url.search = `?token=${encodeURIComponent(token)}`;
  return url.toString();
}

function formatTime(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
}

export default function RideChat({ rideId, currentUser }) {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);
  const listRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, []);

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await api.get(`/rides/${rideId}/messages`);
      setMessages(data.messages || []);
      setTimeout(scrollToBottom, 50);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [rideId, scrollToBottom]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  useEffect(() => {
    const token = localStorage.getItem("ck_token");
    if (!token) return;
    let ws;
    let pingInterval;
    let closed = false;
    const connect = () => {
      try {
        ws = new WebSocket(getBackendWsUrl(rideId, token));
      } catch {
        return;
      }
      wsRef.current = ws;
      ws.onopen = () => {
        setConnected(true);
        pingInterval = setInterval(() => {
          if (ws.readyState === 1) ws.send("ping");
        }, 25000);
      };
      ws.onmessage = (evt) => {
        if (evt.data === "pong") return;
        try {
          const payload = JSON.parse(evt.data);
          if (payload.type === "message" && payload.data) {
            setMessages((prev) => {
              if (prev.some((m) => m.id === payload.data.id)) return prev;
              const next = [...prev, payload.data];
              setTimeout(scrollToBottom, 50);
              return next;
            });
          }
        } catch { /* ignore */ }
      };
      ws.onclose = () => {
        setConnected(false);
        if (pingInterval) clearInterval(pingInterval);
        if (!closed) setTimeout(connect, 3000);
      };
      ws.onerror = () => {
        try { ws.close(); } catch { /* ignore */ }
      };
    };
    connect();
    return () => {
      closed = true;
      if (pingInterval) clearInterval(pingInterval);
      if (wsRef.current) {
        try { wsRef.current.close(); } catch { /* ignore */ }
      }
    };
  }, [rideId, scrollToBottom]);

  const send = async (e) => {
    e.preventDefault();
    const value = text.trim();
    if (!value) return;
    setSending(true);
    try {
      const { data } = await api.post(`/rides/${rideId}/messages`, { text: value });
      // Optimistically include if WS didn't deliver yet
      setMessages((prev) => (prev.some((m) => m.id === data.id) ? prev : [...prev, data]));
      setText("");
      setTimeout(scrollToBottom, 50);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="brut-card p-5 sm:p-6" data-testid="ride-chat-card">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading text-xl font-bold flex items-center gap-2">
          <MessageCircle className="w-5 h-5 text-[#1A2A6C]" /> Chat
        </h2>
        <span
          className={`brut-chip ${connected ? "bg-[#D4F1ED] text-[#26B5A8]" : "bg-[#F5F6FA] text-[#8A91A8]"}`}
          data-testid="chat-connection-status"
        >
          <span className={`w-1.5 h-1.5 rounded-full ${connected ? "bg-[#26B5A8]" : "bg-[#8A91A8]"}`} />
          {connected ? "Online" : "Offline"}
        </span>
      </div>

      <div
        ref={listRef}
        className="space-y-2.5 max-h-80 overflow-y-auto pr-1 mb-4 border border-[#ECEEF5] bg-[#F8F9FC] rounded-2xl p-3"
        data-testid="chat-messages-list"
      >
        {messages.length === 0 ? (
          <p className="text-sm text-[#8A91A8] text-center py-8">Nessun messaggio. Scrivi per primo!</p>
        ) : (
          messages.map((m) => {
            const mine = currentUser && m.user_id === currentUser.id;
            const c = pickColor(m.user_id || m.user_name);
            return (
              <div key={m.id} className={`flex gap-2 ${mine ? "flex-row-reverse" : ""}`} data-testid={`chat-msg-${m.id}`}>
                <div
                  className="w-8 h-8 grid place-items-center rounded-full font-heading font-bold text-xs shrink-0"
                  style={{ backgroundColor: c.bg, color: c.text }}
                >
                  {(m.user_name || "?").charAt(0).toUpperCase()}
                </div>
                <div className={`max-w-[78%] ${mine ? "items-end" : "items-start"} flex flex-col`}>
                  {!mine && <p className="text-[11px] font-semibold text-[#8A91A8] mb-0.5 ml-1">{m.user_name}</p>}
                  <div
                    className={`px-3 py-2 rounded-2xl text-sm whitespace-pre-wrap break-words ${
                      mine
                        ? "bg-[#1A2A6C] text-white rounded-br-md"
                        : "bg-white border border-[#ECEEF5] text-[#1A1D29] rounded-bl-md"
                    }`}
                  >
                    {m.text}
                  </div>
                  <span className="text-[10px] text-[#8A91A8] mt-0.5 mx-1">{formatTime(m.created_at)}</span>
                </div>
              </div>
            );
          })
        )}
      </div>

      <form onSubmit={send} className="flex gap-2" data-testid="chat-form">
        <input
          type="text"
          className="brut-input flex-1"
          placeholder="Scrivi un messaggio…"
          value={text}
          onChange={(e) => setText(e.target.value)}
          maxLength={1000}
          data-testid="chat-input"
        />
        <button
          type="submit"
          className="brut-btn shrink-0"
          disabled={sending || !text.trim()}
          data-testid="chat-send-button"
          aria-label="Invia"
        >
          <Send className="w-4 h-4" />
        </button>
      </form>
      <p className="text-[11px] text-[#8A91A8] mt-2">I messaggi si auto-cancellano dopo 30 giorni.</p>
    </div>
  );
}
